import { COLS, HIDDEN_ROWS, PIECE_COLORS, SHAPES, VISIBLE_ROWS } from "./constants";
import {
  drawParticles,
  type BoardLayout,
  type TetrisEffects,
} from "./effects";
import type { EngineSnapshot, PieceId } from "./types";

export interface ThemePalette {
  dark: boolean;
  board: string;
  boardEdge: string;
  grid: string;
  ghost: string;
  vignette: string;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function drawCrystal(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  piece: PieceId,
  options?: { ghost?: boolean; glow?: number }
): void {
  const colors = PIECE_COLORS[piece];
  const pad = size * 0.08;
  const s = size - pad * 2;
  const px = x + pad;
  const py = y + pad;
  const radius = s * 0.18;
  const ghost = options?.ghost ?? false;

  ctx.save();
  if (!ghost) {
    ctx.shadowColor = colors.base;
    ctx.shadowBlur = size * (0.28 + (options?.glow ?? 0));
  }

  const gradient = ctx.createLinearGradient(px, py, px + s, py + s);
  gradient.addColorStop(0, ghost ? `${colors.light}33` : colors.light);
  gradient.addColorStop(0.42, ghost ? `${colors.base}22` : colors.base);
  gradient.addColorStop(1, ghost ? `${colors.dark}11` : colors.dark);
  ctx.fillStyle = gradient;
  roundRect(ctx, px, py, s, s, radius);
  ctx.fill();

  if (!ghost) {
    ctx.shadowBlur = 0;
    const shine = ctx.createLinearGradient(px, py, px, py + s * 0.55);
    shine.addColorStop(0, "rgba(255,255,255,0.55)");
    shine.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = shine;
    roundRect(ctx, px + s * 0.08, py + s * 0.08, s * 0.84, s * 0.38, radius);
    ctx.fill();

    ctx.strokeStyle = "rgba(255,255,255,0.35)";
    ctx.lineWidth = Math.max(1, size * 0.04);
    roundRect(ctx, px + 0.5, py + 0.5, s - 1, s - 1, radius);
    ctx.stroke();

    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.beginPath();
    ctx.arc(px + s * 0.28, py + s * 0.26, s * 0.07, 0, Math.PI * 2);
    ctx.fill();
  } else {
    ctx.shadowBlur = 0;
    ctx.strokeStyle = `${colors.light}88`;
    ctx.lineWidth = Math.max(1, size * 0.06);
    roundRect(ctx, px, py, s, s, radius);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawPiecePreview(
  ctx: CanvasRenderingContext2D,
  piece: PieceId | null,
  size: number,
  dark: boolean
): void {
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = dark ? "rgba(15,23,42,0.35)" : "rgba(255,255,255,0.35)";
  roundRect(ctx, 0, 0, size, size, size * 0.16);
  ctx.fill();
  if (!piece) return;

  const matrix = SHAPES[piece][0] ?? [];
  const rows = matrix.length;
  const cols = matrix[0]?.length ?? 0;
  const cell = Math.floor(size / Math.max(rows, cols, 4) * 0.78);
  const totalW = cols * cell;
  const totalH = rows * cell;
  const ox = (size - totalW) / 2;
  const oy = (size - totalH) / 2;
  matrix.forEach((line, row) => {
    line.forEach((filled, col) => {
      if (!filled) return;
      drawCrystal(ctx, ox + col * cell, oy + row * cell, cell, piece);
    });
  });
}

export function drawFrame(
  ctx: CanvasRenderingContext2D,
  snapshot: EngineSnapshot,
  effects: TetrisEffects,
  layout: BoardLayout,
  theme: ThemePalette,
  time: number
): void {
  const { originX, originY, cell, width, height } = layout;
  const shakeX = (Math.random() - 0.5) * effects.shake * 3.2;
  const shakeY = (Math.random() - 0.5) * effects.shake * 3.2;

  ctx.save();
  ctx.translate(shakeX, shakeY);

  const bg = ctx.createLinearGradient(originX, originY, originX, originY + height);
  if (theme.dark) {
    bg.addColorStop(0, "#101828");
    bg.addColorStop(1, "#07111d");
  } else {
    bg.addColorStop(0, "#eef3f8");
    bg.addColorStop(1, "#dbe4ee");
  }
  ctx.fillStyle = bg;
  roundRect(ctx, originX - 6, originY - 6, width + 12, height + 12, 16);
  ctx.fill();

  ctx.strokeStyle = theme.boardEdge;
  ctx.lineWidth = 2;
  roundRect(ctx, originX - 6, originY - 6, width + 12, height + 12, 16);
  ctx.stroke();

  ctx.save();
  ctx.beginPath();
  roundRect(ctx, originX, originY, width, height, 10);
  ctx.clip();

  for (let row = 0; row < VISIBLE_ROWS; row += 1) {
    for (let col = 0; col < COLS; col += 1) {
      ctx.fillStyle = theme.grid;
      ctx.fillRect(
        originX + col * cell + 0.5,
        originY + row * cell + 0.5,
        cell - 1,
        cell - 1
      );
    }
  }

  const sweep = (Math.sin(time / 900) + 1) / 2;
  const sweepGrad = ctx.createLinearGradient(
    originX,
    originY + height * sweep,
    originX,
    originY + height * sweep + 40
  );
  sweepGrad.addColorStop(0, "transparent");
  sweepGrad.addColorStop(0.5, theme.dark ? "rgba(125,211,252,0.05)" : "rgba(255,255,255,0.18)");
  sweepGrad.addColorStop(1, "transparent");
  ctx.fillStyle = sweepGrad;
  ctx.fillRect(originX, originY, width, height);

  snapshot.board.forEach((line, row) => {
    if (row < HIDDEN_ROWS) return;
    line.forEach((cellId, col) => {
      if (!cellId) return;
      const clearing = effects.clearingRows.some((item) => item.y === row);
      drawCrystal(
        ctx,
        originX + col * cell,
        originY + (row - HIDDEN_ROWS) * cell,
        cell,
        cellId,
        { glow: clearing ? 0.6 : 0 }
      );
    });
  });

  if (snapshot.active) {
    const ghost = { ...snapshot.active, y: snapshot.ghostY };
    const matrix = SHAPES[ghost.id][ghost.rotation] ?? [];
    matrix.forEach((line, row) => {
      line.forEach((filled, col) => {
        if (!filled) return;
        const gy = ghost.y + row;
        if (gy < HIDDEN_ROWS) return;
        drawCrystal(
          ctx,
          originX + (ghost.x + col) * cell,
          originY + (gy - HIDDEN_ROWS) * cell,
          cell,
          ghost.id,
          { ghost: true }
        );
      });
    });

    const activeMatrix = SHAPES[snapshot.active.id][snapshot.active.rotation] ?? [];
    activeMatrix.forEach((line, row) => {
      line.forEach((filled, col) => {
        if (!filled) return;
        const ay = snapshot.active!.y + row;
        if (ay < HIDDEN_ROWS) return;
        drawCrystal(
          ctx,
          originX + (snapshot.active!.x + col) * cell,
          originY + (ay - HIDDEN_ROWS) * cell,
          cell,
          snapshot.active!.id,
          { glow: 0.25 }
        );
      });
    });
  }

  drawParticles(ctx, effects);

  if (effects.flare > 0) {
    const flare = ctx.createRadialGradient(
      effects.impactX || originX + width / 2,
      effects.impactY || originY + height * 0.85,
      4,
      effects.impactX || originX + width / 2,
      effects.impactY || originY + height * 0.85,
      90 * effects.flare
    );
    flare.addColorStop(0, `rgba(255,255,255,${0.55 * effects.flare})`);
    flare.addColorStop(0.4, `rgba(186,230,253,${0.22 * effects.flare})`);
    flare.addColorStop(1, "transparent");
    ctx.fillStyle = flare;
    ctx.fillRect(originX, originY, width, height);
  }

  if (effects.flash > 0) {
    ctx.fillStyle = `rgba(255,255,255,${effects.flash * 0.35})`;
    ctx.fillRect(originX, originY, width, height);
  }

  const vignette = ctx.createRadialGradient(
    originX + width / 2,
    originY + height / 2,
    width * 0.2,
    originX + width / 2,
    originY + height / 2,
    width * 0.78
  );
  vignette.addColorStop(0, "transparent");
  vignette.addColorStop(1, theme.vignette);
  ctx.fillStyle = vignette;
  ctx.fillRect(originX, originY, width, height);

  ctx.restore();
  ctx.restore();
}
